import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import { trackCTA, trackLead } from "../../lib/analytics";
import { buildSandboxUrl } from "../../lib/sandboxDemo.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * Page hero. Same shape as the four sibling heroes — eyebrow, H1 at
 * `heading-9vw`, supporting line, two actions — so the five SEO pages read as
 * chapters of one story rather than five designs.
 *
 * The secondary action is the sandbox, not the sample invitation. Seating is a
 * COUPLE-side workspace: `demo-heirloom` is a guest-facing invitation and
 * contains no seating surface at all, so linking it here would send a visitor
 * looking for a seating board to a page that has none. `/try` mints a real but
 * disposable Heirloom event that is seeded with tables and assignments
 * (api/services/sandbox-seed.service.mjs), which makes it the only honest way
 * to show this feature to somebody who has not paid.
 *
 * `trackLead` fires alongside `trackCTA` — `/try` provisions a real event,
 * which is this site's own definition of a lead, and is exactly how the
 * homepage pricing card and /wedding-planning already treat the same URL.
 * `rel="noopener noreferrer nofollow"`: `/try` is a provisioning endpoint, not
 * a page, so no link to it on this site is followable.
 *
 * The hero carries a note the sibling heroes do not, because the offer here
 * needs one: a visitor must know before clicking that the board they are about
 * to open is a disposable example rather than an account they are creating.
 *
 * No `data-w-id` and no inline `opacity: 0`, for the reason documented on the
 * photo-sharing hero: the H1 must never depend on the Webflow interaction
 * engine to become visible.
 */
const SeatingHero = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  return (
    <header className="section wps-hero">
      <div className="container">
        <p className="wps-eyebrow">{t("weddingSeating.hero.eyebrow")}</p>
        <h1 className="heading-9vw wps-hero-heading">
          {t("weddingSeating.hero.heading")}
        </h1>
        <p className="body-20 wps-hero-body">{t("weddingSeating.hero.body")}</p>
        <div className="wps-hero-actions">
          <Button
            href={localize("/#pricing")}
            text={t("weddingSeating.hero.ctaPrimary")}
            className="button-lg"
            onClick={(e) => {
              trackCTA("wedding_seating_hero", {
                ...analyticsContext,
                ctaName: "see_plans",
              });
              scrollToPlans(e);
            }}
          />
          <a
            className="wps-text-link"
            href={buildSandboxUrl(shortLanguage)}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={() => {
              const ctx = { ...analyticsContext, ctaName: "start_trial" };
              trackCTA("wedding_seating_hero", ctx);
              trackLead("wedding_seating_hero", ctx);
            }}
          >
            {t("weddingSeating.hero.ctaSecondary")}
          </a>
        </div>
        <p className="wps-note">{t("weddingSeating.hero.note")}</p>
      </div>
    </header>
  );
};

export default SeatingHero;
